import type { MastrAktualisieren, MastrDaten } from "../api/typen";
import { parseLeistung, pruefePlz } from "./validierung";

// Spiegelt die MaStR-Prüfungen aus backend/app/services/validierung.py.

export type MastrFeld =
  | "vorname" | "nachname" | "geburtsdatum" | "telefonnummer"
  | "betreiber_strasse" | "betreiber_hausnummer" | "betreiber_plz" | "betreiber_ort" | "marktakteur_mastr_nummer"
  | "anzahl_module" | "modulleistung_wp" | "wechselrichterleistung_kw" | "inbetriebnahme_datum"
  | "speicher_vorhanden" | "speicher_kapazitaet_kwh"
  | "netzbetreiber" | "netzebene"
  | "netzbetreiber_status" | "netzbetreiber_eingereicht_am" | "netzbetreiber_referenz" | "netzbetreiber_pruefphase";

export type MastrForm = Record<MastrFeld, string>;

export interface MastrFeldDef {
  name: MastrFeld;
  label: string;
  art: "text" | "zahl" | "ganzzahl" | "datum" | "janein" | "auswahl";
  optionen?: { wert: string; text: string }[];
  einheit?: string;
  platzhalter?: string;
  pflicht?: boolean;
  breit?: boolean;
  pruefen?: (wert: string) => string | null;
}

function bereich(minimum: number, maximum: number, text: string) {
  return (roh: string) => {
    if (!roh.trim()) return null;
    const wert = parseLeistung(roh);
    if (wert === null || Number.isNaN(wert)) return "Bitte eine Zahl eingeben";
    return wert >= minimum && wert <= maximum ? null : text;
  };
}

const ganzzahl = (text: string) => (roh: string) => {
  if (!roh.trim()) return null;
  return /^\d+$/.test(roh.trim()) && Number(roh) >= 1 && Number(roh) <= 10_000 ? null : text;
};

function datumBereich(pruefung: (d: Date) => string | null) {
  return (roh: string) => {
    if (!roh) return null;
    const d = new Date(`${roh}T00:00:00`);
    return Number.isNaN(d.getTime()) ? "Ungültiges Datum" : pruefung(d);
  };
}

export const pruefeGeburtsdatum = datumBereich((d) => {
  const heute = new Date();
  const grenze = new Date(heute.getFullYear() - 16, heute.getMonth(), heute.getDate());
  return d < new Date("1900-01-01T00:00:00") || d > grenze
    ? "Geburtsdatum ist unplausibel (Anlagenbetreiber muss mindestens 16 Jahre alt sein)"
    : null;
});

export const pruefeInbetriebnahme = datumBereich((d) => {
  const grenze = new Date();
  grenze.setDate(grenze.getDate() + 730);
  if (d < new Date("2000-01-01T00:00:00")) return "Inbetriebnahmedatum ist unplausibel (vor 2000)";
  return d > grenze ? "Inbetriebnahmedatum liegt mehr als 2 Jahre in der Zukunft" : null;
});

export function pruefeSeeNummer(roh: string): string | null {
  const wert = roh.replace(/\s/g, "").toUpperCase();
  if (!wert) return null;
  return /^SEE\d{10,14}$/.test(wert) ? null : "MaStR-Nummer der Einheit beginnt mit „SEE“ und hat danach 12 Ziffern";
}

const pruefeMarktakteur = (roh: string) => {
  const wert = roh.replace(/\s/g, "").toUpperCase();
  if (!wert) return null;
  return /^[A-Z]{3}\d{10,14}$/.test(wert) ? null : "MaStR-Nummer des Marktakteurs: 3 Buchstaben + 12 Ziffern (z. B. ABR…)";
};

const pruefeTelefon = (roh: string) =>
  !roh.trim() || /^\+?[0-9][0-9 ()/-]{5,24}$/.test(roh.trim()) ? null : "Telefonnummer: nur Ziffern, Leerzeichen, + ( ) / -";

const pruefeHausnummer = (roh: string) =>
  !roh.trim() || /^\d{1,5}\s*[a-zA-Z]?(\s*[-/]\s*\d{1,5}\s*[a-zA-Z]?)?$/.test(roh.trim()) ? null : "Hausnummer z. B. 7, 7a oder 7-9";

export const BETREIBER_FELDER: MastrFeldDef[] = [
  { name: "vorname", label: "Vorname", art: "text", pflicht: true },
  { name: "nachname", label: "Nachname", art: "text", pflicht: true },
  { name: "geburtsdatum", label: "Geburtsdatum", art: "datum", pflicht: true, pruefen: pruefeGeburtsdatum },
  { name: "telefonnummer", label: "Telefon", art: "text", platzhalter: "optional", pruefen: pruefeTelefon },
  { name: "betreiber_strasse", label: "Straße", art: "text", pflicht: true },
  { name: "betreiber_hausnummer", label: "Hausnummer", art: "text", pflicht: true, pruefen: pruefeHausnummer },
  { name: "betreiber_plz", label: "PLZ", art: "text", pflicht: true, pruefen: pruefePlz },
  { name: "betreiber_ort", label: "Ort", art: "text", pflicht: true },
  { name: "marktakteur_mastr_nummer", label: "MaStR-Nr. Anlagenbetreiber", art: "text", platzhalter: "falls schon registriert (ABR…)", breit: true, pruefen: pruefeMarktakteur },
];

export const TECHNIK_FELDER: MastrFeldDef[] = [
  { name: "anzahl_module", label: "Anzahl Module", art: "ganzzahl", pflicht: true, pruefen: ganzzahl("Modulanzahl muss zwischen 1 und 10.000 liegen") },
  { name: "modulleistung_wp", label: "Modulleistung", art: "zahl", einheit: "Wp", pruefen: bereich(50, 1000, "Modulleistung muss zwischen 50 und 1.000 Wp liegen") },
  { name: "wechselrichterleistung_kw", label: "Wechselrichterleistung", art: "zahl", einheit: "kW", pflicht: true, pruefen: bereich(0.1, 1000, "Wechselrichterleistung muss zwischen 0,1 und 1.000 kW liegen") },
  { name: "inbetriebnahme_datum", label: "Inbetriebnahmedatum", art: "datum", pflicht: true, pruefen: pruefeInbetriebnahme },
  {
    name: "speicher_vorhanden", label: "Batteriespeicher", art: "janein",
    optionen: [{ wert: "", text: "unbekannt" }, { wert: "ja", text: "Ja" }, { wert: "nein", text: "Nein" }],
  },
  { name: "speicher_kapazitaet_kwh", label: "Nutzbare Speicherkapazität", art: "zahl", einheit: "kWh", pruefen: bereich(0.1, 2000, "Speicherkapazität muss zwischen 0,1 und 2.000 kWh liegen") },
];

export const NETZ_FELDER: MastrFeldDef[] = [
  { name: "netzbetreiber", label: "Netzbetreiber", art: "text", pflicht: true, breit: true },
  {
    name: "netzebene", label: "Spannungsebene", art: "auswahl",
    optionen: [{ wert: "", text: "–" }, { wert: "Niederspannung", text: "Niederspannung" }, { wert: "Mittelspannung", text: "Mittelspannung" }],
  },
  {
    name: "netzbetreiber_status", label: "Anmeldung beim Netzbetreiber", art: "auswahl",
    optionen: [
      { wert: "nicht_begonnen", text: "Nicht begonnen" },
      { wert: "eingereicht", text: "Eingereicht" },
      { wert: "bestaetigt", text: "Bestätigt" },
    ],
  },
  { name: "netzbetreiber_eingereicht_am", label: "Eingereicht am", art: "datum" },
  { name: "netzbetreiber_referenz", label: "Vorgangsnummer / Referenz", art: "text", platzhalter: "optional", breit: true },
  {
    name: "netzbetreiber_pruefphase", label: "Phase beim Netzbetreiber (für den Status nach außen)", art: "auswahl",
    optionen: [
      { wert: "", text: "– nicht angegeben –" },
      { wert: "anschluss", text: "Anschlussprüfung" },
      { wert: "zaehlerdaten", text: "Zählerdatenprüfung" },
      { wert: "abschluss", text: "Abschlussprüfung" },
    ],
    breit: true,
  },
];

export const ALLE_MASTR_FELDER = [...BETREIBER_FELDER, ...TECHNIK_FELDER, ...NETZ_FELDER];
export const MASTR_LABEL: Record<string, string> = {
  ...Object.fromEntries(ALLE_MASTR_FELDER.map((f) => [f.name, f.label])),
  leistung_kwp: "Leistung (kWp)",
};

function text(wert: string | number | boolean | null | undefined): string {
  if (wert === null || wert === undefined) return "";
  if (typeof wert === "boolean") return wert ? "ja" : "nein";
  return typeof wert === "number" ? String(wert).replace(".", ",") : wert;
}

export function formAusMastr(d: MastrDaten): MastrForm {
  return Object.fromEntries(ALLE_MASTR_FELDER.map((f) => [f.name, text(d[f.name as keyof MastrDaten] as never)])) as MastrForm;
}

export function apiWert(def: MastrFeldDef, roh: string): string | number | boolean | null {
  const wert = roh.trim();
  if (!wert) return def.name === "netzbetreiber_status" ? "nicht_begonnen" : null;
  switch (def.art) {
    case "zahl":
      return parseLeistung(wert);
    case "ganzzahl":
      return Number(wert);
    case "janein":
      return wert === "ja";
    default:
      if (def.name === "betreiber_plz" || def.name === "marktakteur_mastr_nummer") return wert.replace(/\s/g, "").toUpperCase();
      return wert.replace(/\s+/g, " ");
  }
}

export function validiereMastr(form: MastrForm): Partial<Record<MastrFeld, string>> {
  const fehler: Partial<Record<MastrFeld, string>> = {};
  for (const def of ALLE_MASTR_FELDER) {
    const meldung = def.pruefen?.(form[def.name]);
    if (meldung) fehler[def.name] = meldung;
  }
  return fehler;
}

export function geaenderteMastrFelder(basis: MastrForm, aktuell: MastrForm): MastrFeld[] {
  return ALLE_MASTR_FELDER.filter((d) => apiWert(d, basis[d.name]) !== apiWert(d, aktuell[d.name])).map((d) => d.name);
}

export function mastrBody(aktuell: MastrForm, felder: MastrFeld[], version: number): MastrAktualisieren & { version: number } {
  const body: Record<string, unknown> = { version };
  const defs = new Map(ALLE_MASTR_FELDER.map((d) => [d.name, d]));
  for (const name of felder) {
    const def = defs.get(name);
    if (def) body[name] = apiWert(def, aktuell[name]);
  }
  return body as MastrAktualisieren & { version: number };
}

/** „Lindenallee 7“ → ["Lindenallee", "7"] – wie trenne_strasse_hausnummer im Backend. */
export function trenneStrasse(adresse: string): [string, string] {
  const treffer = /^(.*?\D)\s*(\d{1,5}\s*[a-zA-Z]?(\s*[-/]\s*\d{1,5}\s*[a-zA-Z]?)?)$/.exec(adresse.trim());
  return treffer ? [treffer[1]!.trim().replace(/,$/, ""), treffer[2]!.replace(/\s+/g, "")] : [adresse.trim(), ""];
}
